import {useMemo} from 'react';

import {useRunsData, useRunSetsQuery} from '../../../../state/runs/hooks';
import * as ViewHooks from '../../../../state/views/hooks';
import * as PanelActions from '../../../../state/views/panel/actions';
import {makeContext} from '../../../MakeContext/makeContext';
import {panelViews} from '../../../PanelBank/types';
import {MediaBrowserPanelConfig, Spec} from '../../../PanelMediaBrowser';
import {useMediaCardType} from '../../../PanelMediaBrowser/useGetMediaCardType';
import {useConfigWithDefaults} from '../../../PanelMediaBrowser/useMediaPanelValues';
import {usePanelsSharedRefsContext} from '../../../Views/contexts/PanelsSharedRefsContext';
import {MediaSettingsDrawerPanelProps} from './types';

export const useMediaSettingsDrawer = ({
  panelRef,
  panelBankSectionConfigRef,
}: MediaSettingsDrawerPanelProps) => {
  const panel = ViewHooks.usePart(panelRef);
  const mediaCardType = useMediaCardType(
    panel.viewType === panelViews.mediaBrowser
      ? (panel?.config as MediaBrowserPanelConfig)?.mediaKeys
      : undefined
  );
  const updateConfig = ViewHooks.useViewAction(panelRef, (ref, configUpdate) =>
    PanelActions.updateConfig(ref, configUpdate, panelBankSectionConfigRef)
  );
  // This is needed because the `updateConfig` action doesn't support unsetting a field
  // since it just merges the update over the old one, so setting a field to undefined
  // doesn't work.
  const unsetConfigField = ViewHooks.useViewAction(
    panelRef,
    (ref, field: keyof MediaBrowserPanelConfig) => {
      const {[field]: _, ...newConfig} =
        panel.config as MediaBrowserPanelConfig; // casting here because we already checked the panel config type
      return PanelActions.setConfig(ref, newConfig, undefined);
    }
  );
  const {customRunColorsRef, runSetRefs, customRunNamesRef} =
    usePanelsSharedRefsContext();
  const customRunColors = ViewHooks.usePart(customRunColorsRef);
  const query = useRunSetsQuery({runSetRefs, customRunNamesRef});
  const config = panel.config as MediaBrowserPanelConfig;
  // We need to load data about the current media shown in the panel so that we can know what to default
  // certain settings to.
  const panelQuery = useMemo(
    () => ({...Spec.transformQuery(query, config), disabled: false}), // transformQuery sets disabled to true for the media slider ramp flag
    [query, config]
  );
  // loading these defaults shouldn't block the settings drawer
  const {data, loading} = useRunsData(panelQuery);
  const {configWithDefaults, inRunPage, hasMasks, hasBoxes} =
    useConfigWithDefaults({
      data,
      query: panelQuery,
      config,
      customRunColors,
      pageQuery: query,
    });
  return useMemo(
    () => ({
      config: configWithDefaults,
      inRunPage,
      hasMasks,
      hasBoundingBoxes: hasBoxes,
      loading,
      mediaCardType,
      updateConfig,
      unsetConfigField,
    }),
    [
      configWithDefaults,
      inRunPage,
      hasMasks,
      hasBoxes,
      loading,
      mediaCardType,
      updateConfig,
      unsetConfigField,
    ]
  );
};

export const {
  Provider: MediaSettingsDrawerContextProvider,
  hook: useMediaSettingsDrawerContext,
} = makeContext<ReturnType<typeof useMediaSettingsDrawer>>({
  contextType: 'MediaSettingsDrawer',
});
